import { useState } from 'react';
import { supabase } from '../lib/supabase';
import type { NewsArticleWithRelations, NewsFilter } from '../types/news';
import slugify from 'slugify';

export function useNews() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchArticles = async (filter?: NewsFilter) => {
    try {
      setLoading(true);
      setError(null);
      // Primeiro buscar os artigos
      let query = supabase
        .from('news_articles')
        .select(`
          *,
          category:news_categories(*),
          tags:news_article_tags(
            news_tags (
              id,
              name,
              slug
            )
          ),
          comments:news_comments(*),
          images:news_images(*)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (filter) {
        if (filter.category) {
          query = query.eq('category.slug', filter.category);
        }
        if (filter.tag) {
          query = query.contains('tags.slug', [filter.tag]);
        }
        if (filter.search) {
          query = query.or(`title.ilike.%${filter.search}%,content.ilike.%${filter.search}%`);
        }
        if (filter.status) {
          query = query.eq('status', filter.status);
        }
        if (filter.featured !== undefined) {
          query = query.eq('is_featured', filter.featured);
        }
        if (filter.breaking !== undefined) {
          query = query.eq('is_breaking', filter.breaking);
        }
      }

      const { data: articles, error: articlesError } = await query;
      if (articlesError) {
        setError(articlesError.message);
        return [];
      }

      if (!articles) {
        return [];
      }

      // Buscar todas as imagens dos artigos
      const { data: allImages, error: imagesError } = await supabase
        .from('news_images')
        .select('*')
        .in('article_id', articles.map(article => article.id))
        .order('position');

      if (imagesError) {
        console.warn('Error fetching images:', imagesError);
        // Continue without images if there's an error
      }

      // Normalizar os dados
      const normalizedData = articles.map(article => ({
        ...article,
        tags: article.tags?.map(tag => tag.news_tags) || [],
        images: allImages?.filter(image => image.article_id === article.id) || []
      }));

      return normalizedData;
    } catch (err: any) {
      console.error('Error fetching articles:', err);
      setError(err.message || 'Error fetching articles');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchArticleBySlug = async (slug: string) => {
    try {
      setLoading(true);
      console.log('Buscando artigo com slug:', slug);
      
      // Primeiro buscar o artigo com todas as relações
      const { data, error: articleError } = await supabase
        .from('news_articles')
        .select(`
          id,
          title,
          slug,
          excerpt,
          content,
          cover_image,
          category_id,
          author_id,
          status,
          is_featured,
          is_breaking,
          views_count,
          reading_time,
          published_at,
          created_at,
          updated_at,
          category:news_categories(
            id,
            name,
            slug,
            description
          ),
          tags:news_article_tags(
            news_tags (
              id,
              name,
              slug
            )
          )
        `)
        .eq('slug', slug)
        .is('deleted_at', null)
        .single();

      if (articleError) {
        console.error('Erro ao buscar artigo:', articleError);
        throw articleError;
      }
      
      if (!data) {
        console.error('Artigo não encontrado');
        throw new Error('Artigo não encontrado');
      }

      console.log('Artigo encontrado:', data);

      // Buscar imagens do artigo
      console.log('A buscar imagens para o artigo:', data.id);
      let imagesData;
      const { data: imageResults, error: imagesError } = await supabase
        .from('news_images')
        .select('id, article_id, url, alt, caption, position, created_at, updated_at')
        .eq('article_id', data.id)
        .order('position');

      if (imagesError) {
        console.error('Erro ao buscar imagens:', imagesError);
        // Não vamos lançar o erro, apenas logar
        console.warn('A continuar sem imagens devido ao erro');
        imagesData = [];
      } else {
        console.log('Imagens encontradas:', imageResults);
        imagesData = imageResults;
      }

      // Normalizar os dados
      const normalizedData = {
        ...data,
        tags: data.tags?.map(tag => tag.news_tags) || [],
        media: imagesData?.map(img => {
          console.log('Processando imagem:', img);
          
          // Se a URL já for pública, não precisa processar
          if (img.url.startsWith('http')) {
            console.log('URL já é pública:', img.url);
            return img;
          }
          
          const publicUrl = supabase.storage
            .from('news')
            .getPublicUrl(img.url)
            .data.publicUrl;
          console.log('URL pública gerada:', publicUrl);
          return {
            ...img,
            url: publicUrl
          };
        }) || []
      };
      
      console.log('Dados normalizados:', normalizedData);

      return normalizedData as NewsArticleWithRelations;
    } catch (err: any) {
      console.error('Erro ao buscar artigo:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createArticle = async (article: Partial<NewsArticleWithRelations>) => {
    try {
      setLoading(true);
      setError(null);

      if (!article.title) {
        throw new Error('Title is required');
      }

      const slug = slugify(article.title, { lower: true, strict: true });
      const now = new Date().toISOString();

      // Remove campos que não existem na tabela
      const { images, media, tags, category, author, ...articleData } = article;

      // Ensure required date fields are set
      const dataToInsert = {
        ...articleData,
        slug,
        created_at: now,
        updated_at: now,
        published_at: article.published_at || now,
        status: article.status || 'draft'
      };

      const { data: newArticle, error: insertError } = await supabase
        .from('news_articles')
        .insert([dataToInsert])
        .select()
        .single();

      if (insertError) throw insertError;

      return newArticle;
    } catch (err: any) {
      console.error('Error creating article:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateArticle = async (id: string, updates: Partial<NewsArticleWithRelations>) => {
    try {
      setLoading(true);
      setError(null);

      if (updates.title) {
        let baseSlug = slugify(updates.title, { lower: true, strict: true });
        let finalSlug = baseSlug;
        let counter = 1;
        
        // Check if slug exists
        while (true) {
          const { data: existingArticle, error: slugCheckError } = await supabase
            .from('news_articles')
            .select('id')
            .eq('slug', finalSlug)
            .neq('id', id)
            .maybeSingle(); // Use maybeSingle() instead of single() to handle no results gracefully

          if (slugCheckError) {
            console.error('Error checking slug:', slugCheckError);
            break; // Continue with current slug if there's an error
          }

          if (!existingArticle) {
            break; // Slug is unique
          }

          // Append counter to make slug unique
          finalSlug = `${baseSlug}-${counter}`;
          counter++;
        }

        updates.slug = finalSlug;
      }

      // Extrair e normalizar os tags e remover campos que não existem na tabela
      const { tags, images, media, category, author, ...articleUpdates } = updates;
      const normalizedTags = tags?.map(tag => {
        // Se o tag já estiver no formato correto, retorna ele mesmo
        if (tag.id && !tag.news_tags) {
          return tag;
        }
        // Se o tag vier do Supabase, ele estará dentro de news_tags
        if (tag.news_tags) {
          return tag.news_tags;
        }
        // Se for um tag direto da seleção
        return tag;
      }).filter(Boolean); // Remove possíveis valores null/undefined
      
      // Atualizar o artigo principal
      const { data: updatedArticle, error: updateError } = await supabase
        .from('news_articles')
        .update({
          ...articleUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Se houver tags para atualizar
      if (normalizedTags && normalizedTags.length > 0) {
        // Primeiro, remover todos os tags existentes
        const { error: deleteError } = await supabase
          .from('news_article_tags')
          .delete()
          .eq('article_id', id);

        if (deleteError) throw deleteError;

        // Depois, inserir os novos tags
        const tagInsertData = normalizedTags.map(tag => ({
          article_id: id,
          tag_id: tag.id
        }));

        const { error: insertError } = await supabase
          .from('news_article_tags')
          .insert(tagInsertData);

        if (insertError) throw insertError;
      }

      // Buscar o artigo atualizado com todas as relações
      const { data: finalArticle, error: fetchError } = await supabase
        .from('news_articles')
        .select(`
          *,
          category:news_categories(*),
          tags:news_article_tags(
            news_tags (
              id,
              name,
              slug
            )
          ),
          media:news_media(
            id,
            type,
            url,
            title,
            alt_text,
            caption,
            position
          ),
          comments:news_comments(*)
        `)
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Normalizar os tags no resultado final
      return {
        ...finalArticle,
        tags: finalArticle.tags?.map(tag => tag.news_tags) || []
      } as NewsArticleWithRelations;

    } catch (err: any) {
      console.error('Error updating article:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteArticle = async (id: string) => {
    try {
      setLoading(true);
      // First delete related records
      const { error: tagsError } = await supabase
        .from('news_article_tags')
        .delete()
        .eq('article_id', id);

      if (tagsError) throw tagsError;

      // Delete comments
      const { error: commentsError } = await supabase
        .from('news_comments')
        .delete()
        .eq('article_id', id);

      if (commentsError) throw commentsError;

      // Delete media
      const { error: mediaError } = await supabase
        .from('news_media')
        .delete()
        .eq('article_id', id);

      if (mediaError) throw mediaError;

      // Finally delete the article
      const { error: deleteError } = await supabase 
        .from('news_articles')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
    } catch (err: any) {
      console.error('Error deleting article:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const incrementViews = async (id: string) => {
    try {
      const { error } = await supabase.rpc('increment_article_views', { article_id: id });
      if (error) throw error;
    } catch (err) {
      console.error('Error incrementing views:', err);
    }
  };

  return {
    loading,
    error,
    fetchArticles,
    fetchArticleBySlug,
    createArticle,
    updateArticle,
    deleteArticle,
    incrementViews
  };
}